import {
  DashboardModes,
  FullscreenWidgetData,
  FullscreenWidgetProps,
  LoadedDashboard,
  OnWidgetDrilldown,
} from '../../types';
import { Locale } from '@ardoq/locale';
import {
  APIChartWidgetAttributes,
  DrilldownScopeData,
  HexColor,
  isViewpointWidgetDatasource,
  NoteWidgetAttributes,
} from '@ardoq/api-types';
import {
  GetComponentLinkHandler,
  HasComponentReadPermission,
} from '@ardoq/report-reader';
import { widgetOperations } from '../operations';
import { Maybe } from '@ardoq/common-helpers';
import { NonEmptyWidget } from '../types';
import { type I18nLocale, t } from '@ardoq/i18n';

type GetFullscreenWidgetModalPropsParams = {
  onClose: VoidFunction;
  isOpen: boolean;
  widgetData: FullscreenWidgetData | null;
  userLocale: Locale;
  dashboardMode: DashboardModes;
  dashboardColors: Record<string, HexColor>;
  onWidgetDrilldown: OnWidgetDrilldown;
  getComponentLinkHandler?: GetComponentLinkHandler;
  getHasComponentReadPermission?: (
    scopeData: DrilldownScopeData
  ) => HasComponentReadPermission;
  dashboardData: Maybe<LoadedDashboard>;
  i18nLocale: I18nLocale;
};

export const getFullscreenWidgetModalProps = ({
  isOpen,
  widgetData,
  onWidgetDrilldown,
  dashboardMode,
  getHasComponentReadPermission,
  getComponentLinkHandler,
  dashboardColors,
  onClose,
  userLocale,
  dashboardData,
  i18nLocale,
}: GetFullscreenWidgetModalPropsParams): FullscreenWidgetProps => {
  const commonProps = {
    onClose,
    isOpen,
    userLocale,
    dashboardMode,
    dashboardId: dashboardData?._id ?? undefined,
    i18nLocale,
  };

  const isNoteWidget = widgetOperations.isReadNoteWidgetData(widgetData);

  if (isNoteWidget) {
    return {
      ...commonProps,
      isNoteWidget: true,
      widgetData: widgetData as NoteWidgetAttributes,
      widgetName: t(
        i18nLocale,
        'dashboard:fullscreenWidget.noteWidgetName',
        'Note'
      ),
      isNonEmptyChartWidget: false,
    };
  }

  const isNonEmptyChartWidget =
    widgetOperations.isReadWidgetData(widgetData) &&
    widgetOperations.isNonEmptyWidgetData(widgetData);

  const activeLinkedDashboardFilters =
    widgetOperations.getActiveWidgetLinkedDashboardFilters({
      dashboardData,
      widgetData,
    });

  if (isNonEmptyChartWidget) {
    return {
      ...commonProps,
      isNoteWidget: false,
      isNonEmptyChartWidget,
      hasViewpointDatasource: isViewpointWidgetDatasource(
        widgetData.datasource
      ),
      onWidgetDrilldown,
      getComponentLinkHandler,
      getHasComponentReadPermission,
      widgetData,
      activeLinkedDashboardFilters,
      widgetName: widgetData.name,
      colors: dashboardColors,
      chartData: getFullscreenWidgetData(widgetData),
      openWidgetDataSource: () => {
        if (isViewpointWidgetDatasource(widgetData.datasource)) return;
        onWidgetDrilldown({
          datasource: widgetData.datasource,
          name: widgetData.name,
          widgetType: widgetData.viewType,
          filters: widgetOperations.mergeDashboardAndWidgetFilters(
            widgetData.filters ?? {},
            activeLinkedDashboardFilters ?? []
          ),
          dashboardId: dashboardData?._id,
        });
        onClose();
      },
    };
  }

  // should never happen
  return {
    isOpen: false,
    widgetData: null,
    onClose,
  };
};

// makes numbers much more readable when in fullscreen mode
const increaseNumberFontSize = (
  widgetData: NonEmptyWidget<APIChartWidgetAttributes>
): NonEmptyWidget<APIChartWidgetAttributes> => ({
  ...widgetData,
  layout: { ...widgetData.layout, w: 5 },
});

// function to modify widget configs for better fullscreen viewing experience
const getFullscreenWidgetData = (
  widgetData: NonEmptyWidget<APIChartWidgetAttributes>
): NonEmptyWidget<APIChartWidgetAttributes> => {
  if (widgetOperations.isNumberWidgetData(widgetData)) {
    return increaseNumberFontSize(widgetData);
  }
  return widgetData;
};
